import React, {
  ChangeEvent,
  useRef,
  useState,
  SyntheticEvent,
  useEffect,
} from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  useTheme,
  MenuItem,
  Select,
  TextField,
  Menu,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { StyledBox } from "../../../styled";
import UploadedImage from "./UploadedImage";
import { TYPE_UPLOAD_MODAL } from "../../../constants";
import {
  fetchProjectLabels,
  fetchProjectLabelById,
  addProjectLabels,
  editProjectLabel,
  deleteProjectLabel,
} from "../../../store/slice/ProjectLabelSlice";
import { openModal } from "../../../store/slice/UISlice";
import VisionStudioSvg from "../../VisionStudioSvg";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { updateLabelColor } from "../../../store/slice/ProjectImagesUploadedSlice";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ProjectLabelItem } from "../../../types/interface/ProjectLabel.interface";
import { selectProjectById } from "../../../store/slice/ProjectSlice";
import fileUploadIcon from "../../../assets/img/file_upload.svg";
import { startSpinner, stopSpinner } from "../../../store/slice/UISlice";
import { fetchImagesNew } from "../../../store/slice/ProjectImagesUploadedSlice";
import { colorSet } from "../../../constants";

interface ImageUploadProps {
  onClose: () => void; // Define the type for onClose
  taskType?: string;
}
interface ProjectLabels {
  value: string;
  label: string;
  color: string;
}

const PreDataSetImageUpload: React.FC<ImageUploadProps> = ({
  onClose,
  taskType,
}) => {
  interface Params extends Record<string, string | undefined> {
    id: string; // 'id' is expected to be a string
  }
  const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const selectedLabelColorRef = useRef<string>("");
  const { labels } = useSelector((state: RootState) => state.projectLabel);
  const dispatch: AppDispatch = useDispatch();
  const [files, setFiles] = useState<File[]>([]);
  const [imageSrcs, setImageSrcs] = useState<string[]>([]);
  const [uploadLength, setUploadLength] = useState<{
    [labelId: string]: number;
  }>({});
  const theme = useTheme();
  const { id } = useParams<Params>();
  const [progress, setProgress] = useState(0);

  const [selectedProjectLabelIdForEdit, setSelectedProjectLabelIdForEdit] =
    useState<string | null>(null);

  const [labelName, setLabelName] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<string>("");
  const [showDiv, setShowDiv] = useState<boolean>(false);
  const [maxLabelCount, setMaxLabelCount] = React.useState(false);
  const handleClickOpen = () => {
    dispatch(openModal({ name: TYPE_UPLOAD_MODAL, taskType: taskType }));
  };
  const handleLabelNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const sanitizedValue = e.target.value
      .replace(/^\s+/, "")
      .replace(/\s+/g, " ");
    setLabelName(sanitizedValue);
  };
  const cancelLabelCreation = () => {
    setSelectedProjectLabelIdForEdit(null);
    //setObjectTracking(false);
    setLabelName("");
    setShowDiv(false);
  };

  const updateUploadLength = (labelId: string, length: number) => {
    setUploadLength((prev) => {
      const currentLength = prev[labelId] || 0;
      const updated = {
        ...prev,
        [labelId]: currentLength + length,
      };
      return updated;
    });
  };

  const handleMenuClose = () => {
    setSelectedProjectLabelIdForEdit(null);
    setAnchorEl(null);
    setCurrentOption(null);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentOption, setCurrentOption] = useState<string | null>(null);
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    optionId: string
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentOption(optionId);
  };
  const labelRef = useRef<HTMLSelectElement>(null);
  const allLabelRef = useRef<ProjectLabelItem[]>([]);

  useEffect(() => {
    const fetchLabels = async () => {
      try {
        if (id) {
          await dispatch(fetchProjectLabels(id))
            .unwrap()
            .then((response) => {
              if (response.length === 0) {
                toast.info("Add label to start annotating!");
              }
              allLabelRef.current = response;
            });
        }
      } catch (error) {
        //toast.error("Failed to fetch label list");
      } finally {
      }
    };

    if (id) {
      dispatch(fetchLabels);
    }
  }, [id]);

  // check the default label to be selected on page load
  useEffect(() => {
    const mappedProjectClass = labels.map((option) => ({
      value: option.id,
      label: option.name,
      color: option.labelColor,
    }));

    setSelectedLabel(mappedProjectClass[0]);
    const labelIndex = labels.length;
    setSelectedColor(colorSet[labelIndex].value);
    if (mappedProjectClass[0]) {
      setSelectedValue(mappedProjectClass[0].value);
    }
  }, [labels]);

  const handleButtonClick = (event: SyntheticEvent) => {
    event.stopPropagation();
    setMaxLabelCount(false);
    setSelectedProjectLabelIdForEdit(null);
    //setObjectTracking(false);
    setLabelName("");
    setShowDiv(true);
  };
  const handleIconClick = (idx: number) => {
    if (fileInputRefs.current[idx]) {
      fileInputRefs.current[idx]?.click(); // Trigger the hidden input's click
    }
  };

  /////Upload start here //////
  const handleFileChange = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLMediaElement>
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const selectedLabel = selectedLabels[index];
    if (!selectedLabel) {
      toast.error("Please select a label before browsing files");
      return;
    }

    const clearFileInput = () => {
      const input = fileInputRefs.current[index];
      if (input) {
        input.value = "";
      }
    };

    try {
      let selectedFiles: File[] = [];
      let ignoredFilesCount = 0;
      let invalidFilesCount = 0;

      const validExtensions = ["png", "jpg", "jpeg"];

      if (event.type === "drop") {
        const droppedFiles = Array.from(
          (event as any).dataTransfer?.files ?? []
        );
        selectedFiles = (droppedFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }

          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      } else {
        const inputFiles = Array.from(
          (event.currentTarget as HTMLInputElement).files ?? []
        );
        selectedFiles = (inputFiles as File[]).filter((file) => {
          const fileExtension = file.name.split(".").pop()?.toLowerCase();
          if (!fileExtension || !validExtensions.includes(fileExtension)) {
            invalidFilesCount++;
            return false;
          }
          if (file.size > 5 * 1024 * 1024) {
            ignoredFilesCount++;
            return false;
          }
          return true;
        });
      }

      if (invalidFilesCount) {
        clearFileInput();
        toast.error(
          `${invalidFilesCount} file${invalidFilesCount === 1 ? "" : "s"
          } have invalid format. Only png, jpg, and jpeg are allowed.`
        );
      }

      if (ignoredFilesCount) {
        clearFileInput();
        toast.error(
          `${ignoredFilesCount} image${ignoredFilesCount === 1 ? "" : "s"
          } exceeded the 5 MB`
        );
      }

      if (selectedFiles.length > 0) {
        const newImageSrcs: string[] = [];
        const newFiles: File[] = [];
        const formData = new FormData();

        // Append other form data
        if (id) {
          formData.append("ProjectId", id);
        } else {
          toast.error("ProjectId is undefined");
          return; // Exit if ProjectId is not defined
        }

        formData.append("Id", "988");
        formData.append("LabelId", selectedLabel);
        for (const file of selectedFiles) {
          newFiles.push(file);
          const reader = new FileReader();

          reader.onloadend = async () => {
            newImageSrcs.push(reader.result as string);
            formData.append("Images", file);

            if (newImageSrcs.length === selectedFiles.length) {
              const totalSize = newFiles.reduce(
                (acc, file) => acc + file.size,
                0
              );

              if (totalSize > 300 * 1024 * 1024) {
                clearFileInput();
                toast.error("Total file size exceeds the 300 MB limit!");
                return; // Stop execution if size limit is exceeded
              }

              setImageSrcs(newImageSrcs);
              setFiles(newFiles);

              // Create a new XMLHttpRequest for uploading images
              const xhr = new XMLHttpRequest();
              xhr.open(
                "POST",
                `${process.env.REACT_APP_SERVER_BASEURL}/api/Images/UploadLabel`
              );
              const token = localStorage.getItem("token");
              xhr.setRequestHeader("Authorization", `${token}`);

              xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                  const percentComplete = (event.loaded / event.total) * 100;
                  const onlyInteger = Math.floor(percentComplete);

                  if (onlyInteger < 100) {
                    setProgress(onlyInteger);
                  }
                }
              };

              // Handle success
              xhr.onload = () => {
                if (xhr.status === 200) {
                  let uploadCount = JSON.parse(xhr.response).length;
                  updateUploadLength(selectedLabel, uploadCount);
                  dispatch(
                    fetchImagesNew({
                      projectId: id,
                      page: 1,
                      count: 40,
                      annotation: "all",
                    })
                  )
                    .unwrap()
                    .then((response) => {
                      if (response.imageUrlModel.length === 0) {
                        dispatch(openModal({ name: TYPE_UPLOAD_MODAL }));
                      }
                    });
                  toast.success(
                    `Image${uploadCount === 1 ? "" : "s"
                    } uploaded successfully!`
                  );
                  setProgress(100);
                  setImageSrcs([]);

                  const parentElement = document.querySelector(".parent-class");
                  if (parentElement) {
                    parentElement.scrollTo({ top: 0, behavior: "smooth" });
                  }
                  //onClose();
                } else {
                  let errorMessage: string;

                  if (typeof xhr.response === "string") {
                    errorMessage = xhr.response;
                  } else if (xhr.response && xhr.response.detail) {
                    errorMessage = xhr.response.detail;
                  } else {
                    errorMessage = "Failed to upload images.";
                  }

                  console.error("Error Response:", xhr.response);
                  toast.error(errorMessage);
                  //onClose();
                }
              };

              // Handle error
              xhr.onerror = () => {
                console.error("Error uploading images:", xhr.response);
                toast.error(xhr.response);
              };

              xhr.send(formData);
            }
          };
          reader.readAsDataURL(file);
        }
      }
    } catch (error) {
      console.error(error, "error");
    }
  };
  ///////Upload end here //////

  const [fileInputs, setFileInputs] = useState<string[]>(["file-1"]);

  const addFileInput = () => {
    if (
      labels.length <= fileInputs.length &&
      labels.length === 2 &&
      taskType === "Binary Classification"
    ) {
      toast.error("Binary Classification allow only Two Blocks.");
      return;
    }
    if (labels.length <= fileInputs.length) {
      toast.error("Please Create Label to Add Another Block.");
      return;
    }
    const newId = `file-${Date.now()}`; // ensures uniqueness even on fast clicks
    setFileInputs([...fileInputs, newId]);
  };
  const [selectedLabels, setSelectedLabels] = useState<{
    [key: number]: string;
  }>({});

  const [selectedLabel, setSelectedLabel] = useState<ProjectLabels>({
    value: "",
    label: "",
    color: "",
  });

  const handleChange = async (
    index: number,
    event: SelectChangeEvent<string>
  ) => {
    const selectedId = event.target.value as string;
    setSelectedLabels((prev) => ({
      ...prev,
      [index]: selectedId,
    }));

    const selectedLabelDetails = labels.find(
      (label) => label.id === selectedId
    );

    if (selectedLabelDetails) {
      const tmpSelectedLabel = {
        value: selectedId,
        label: selectedLabelDetails.name,
        color: selectedLabelDetails.labelColor,
      };
      setSelectedLabel(tmpSelectedLabel);
      if (selectedLabelColorRef.current) {
        selectedLabelColorRef.current = selectedLabelDetails.labelColor;
      }
    }

    labelRef.current!.value = event.target.value;
  };
  const project = useSelector((state: RootState) =>
    id ? selectProjectById(state, id) : undefined
  );

  const { tasks } = useSelector((state: RootState) => state.Tasks);
  const findNameById = (id: string) => {
    const item = tasks.find((entry) => entry.id === id);
    return item ? item.name : undefined;
  };
  const [selectedColor, setSelectedColor] = useState<string>(colorSet[0].value);
  const [deleteProjectLabelModal, setDeleteProjectLabelModal] =
    useState<boolean>(false);
  const handleLabelColorChange = (event: SelectChangeEvent<string>) => {
    setSelectedColor(event.target.value);
  };

  const handleDeleteLabelClose = () => {
    setDeleteProjectLabelModal(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  const handleProjectLabelDelete = async () => {
    try {
      dispatch(startSpinner("Deleting project label"));
      if (currentOption) {
        await dispatch(deleteProjectLabel(currentOption))
          .unwrap()
          .then((result) => {
            handleMenuClose();
            setDeleteProjectLabelModal(false);
            localStorage.setItem("popupOpen", false.toString());
            toast.success("Project Label deleted Successfully");
          });
      }
      dispatch(stopSpinner());
    } catch (e) {
      setDeleteProjectLabelModal(false);
      localStorage.setItem("popupOpen", false.toString());
      handleMenuClose();
      dispatch(stopSpinner());
    }
  };
  const handleEditProjectLabel = async () => {
    if (currentOption) {
      setSelectedProjectLabelIdForEdit(currentOption);
      const response = await dispatch(fetchProjectLabelById(currentOption));
      const editableResult = response.payload[0];
      setLabelName(editableResult.name);
      setSelectedColor(editableResult.labelColor);
      //setObjectTracking(editableResult.objectTracking);
      setShowDiv(true);
      setAnchorEl(null);
    }
  };
  //#region Save Annotation Label
  const saveAnnotationLabel = async (e: React.FormEvent) => {
    toast.dismiss();
    e.preventDefault();
    if (!labelName) {
      toast.error("Please enter Label Name");
      return;
    }

    const taskId: string | undefined = project?.taskTypeId;
    const taskName = taskId ? findNameById(taskId) : "Invalid ID";

    let objectTrackingVariable = false;

    if (taskName?.toLowerCase().includes("tracking")) {
      objectTrackingVariable = true;
    }
    if (!id) {
      console.error("projectId is undefined");
      return;
    }
    const data = {
      id: selectedProjectLabelIdForEdit ? selectedProjectLabelIdForEdit : "",
      name: labelName.trim(),
      description: "Default Description",
      projectId: id,
      type: "Label",
      objectTracking: objectTrackingVariable,
      // slNo: 1,
      labelColor: selectedColor,
    };
    dispatch(startSpinner("Saving project label"));

    try {
      if (selectedProjectLabelIdForEdit) {
        await dispatch(editProjectLabel(data))
          .unwrap()
          .then(() => {
            const data = {
              labelId: selectedProjectLabelIdForEdit,
              color: selectedColor,
            };
            dispatch(updateLabelColor(data));

            toast.success("Project Label updated Successfully");
          });
        //await dispatch(fetchProjectLabels);
      } else {
        await dispatch(addProjectLabels(data))
          .unwrap()
          .then((response) => {
            allLabelRef.current = [...allLabelRef.current, response];
            toast.success("Project Label added Successfully");
          });
      }
    } catch (error) {
      //toast.error("Failed to save Label");
    } finally {
      dispatch(stopSpinner());
      setShowDiv(false);
      //setObjectTracking(false);
      setLabelName("");
      setSelectedProjectLabelIdForEdit(null);
    }
  };

  return (
    <>
      {imageSrcs.length === 0 && (
        <Box sx={{ paddingTop: "7px" }}>
          <Typography
            sx={{
              fontSize: theme.typography.h6.fontSize,
              fontWeight: theme.typography.h5.fontWeight,
              position: "absolute",
              top: "14px",
            }}
          >
            Upload Images
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              position: "absolute",
              right: "55px",
              top: "10px",
            }}
          >
            {progress === 0 && (
              <Button
                id="backButton"
                onClick={handleClickOpen}
                variant="contained"
                color="primary"
                sx={{ ml: 2 }}
              >
                ←
              </Button>
            )}
            <Button
              id="addMoreBlock"
              onClick={addFileInput}
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
            >
              +
            </Button>
          </Box>

          <StyledBox sx={{ backgroundColor: "#FFFFFF", marginTop: "20px" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, 49%)",
                gap: "10px",
                justifyContent: "start",
                //  padding: "10px",
                width: "100%",
                maxHeight: "calc(100vh - 300px)",
                overflowX: "auto",
              }}
            >
              {fileInputs.map((id, idx) => (
                <>
                  <Box
                    key={id}
                    sx={{
                      width: "100%",
                      height: "250px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      display: "block",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div>
                      <Select
                        id="selectLabel"
                        key={id}
                        sx={{
                          paddingTop: "0",
                          width: "100%",
                          background: "#0000001A",
                          borderRadius: "6px",
                          boxShadow:
                            "1.46px 2.92px 2.92px -0.73px #00000040 inset",
                          color: "#ffffff",

                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                            borderColor: "transparent",
                          },
                        }}
                        value={selectedLabels[idx] || ""}
                        onChange={(e) => handleChange(idx, e)}
                        inputRef={labelRef}
                        displayEmpty
                        inputProps={{ "aria-label": "Without label" }}
                        size="small"
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: "300px",
                              width: 250,
                              position: "relative",
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          const selectedLabel = labels.find(
                            (label:any) => label.id === selected
                          );
                          return selectedLabel ? (
                            <Box
                              id="addSelectLabel"
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              <span
                                style={{
                                  height: "17px",
                                  width: "17px",
                                  background: selectedLabel.labelColor,
                                  borderRadius: "4px",
                                  marginRight: "10px",
                                  display: "inline-block",
                                }}
                              ></span>
                              <abbr
                                style={{
                                  textOverflow: "ellipsis",
                                  textDecoration: "none",
                                  width: "215px",
                                  overflow: "hidden",
                                }}
                              >
                                {selectedLabel.name}
                              </abbr>
                            </Box>
                          ) : (
                            <em>
                              {labels.length > 0
                                ? "Select / Add Label"
                                : "Add Label"}
                            </em>
                          );
                        }}
                      >
                        {labels.length === 2 &&
                          taskType?.toLowerCase() ===
                          "binary classification" && (
                            <Box
                              sx={{
                                backgroundColor: "#FFF3CD",
                                color: "#856404",
                                padding: "8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                textAlign: "center",
                                border: "1px solid #ffeeba",
                              }}
                            >
                              Only two labels are allowed in a binary
                              classification project.
                            </Box>
                          )}
                        <MenuItem
                          disabled={
                            labels.length > 1 &&
                            taskType?.toLowerCase() === "binary classification"
                          }
                          value="button"
                          sx={{
                            display: "block",
                            lineHeight: "2",
                            padding: "0",
                            position: "sticky", // Make this sticky at the bottom
                            top: "0",
                            zIndex: 1,
                          }}
                        >
                          <Box justifyContent="center">
                            <Button
                              disabled={
                                labels.length > 1 &&
                                taskType?.toLowerCase() ===
                                "binary classification"
                              }
                              variant="contained"
                              sx={{
                                lineHeight: "2",
                                background: "#EEECEC",
                                width: "100%",
                                padding: "10px",
                                color: "#1D97F3",
                                boxShadow: "none",
                                borderRadius: "0", // Ensures background color over other items
                              }}
                              onClick={handleButtonClick}
                            >
                              + Add Label
                            </Button>
                          </Box>
                        </MenuItem>

                        {showDiv && (
                          <div
                            style={{
                              background: "white",
                              position: "sticky",
                              top: "47px",
                              zIndex: "11",
                            }}
                          >
                            <form
                              onSubmit={saveAnnotationLabel}
                              style={{ boxShadow: "0 2px 11px #00000047" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  paddingLeft: "17px",
                                }}
                              >
                                <TextField
                                  id="standard-basic"
                                  label="id"
                                  variant="standard"
                                  type="hidden"
                                  value={selectedProjectLabelIdForEdit}
                                />
                                <span
                                  style={{
                                    height: "17px",
                                    width: "17px",
                                    background: selectedColor,
                                    borderRadius: "4px",
                                    marginRight: "10px",
                                  }}
                                >
                                  <Select
                                    value={selectedColor}
                                    onChange={handleLabelColorChange}
                                    sx={{
                                      padding: "0",
                                      height: "15px",
                                      width: "17px",
                                      border: "none",
                                      "& .MuiInputBase-input": {
                                        border: "none",
                                        padding: "0!important",
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                        padding: "0",
                                      },
                                    }}
                                    IconComponent={() => null} // This removes the arrow
                                    MenuProps={{
                                      PaperProps: {
                                        style: {
                                          maxHeight: "150px",
                                          width: 250,
                                        },
                                      },
                                    }}
                                    renderValue={(selected) => {
                                      return (
                                        <span
                                          style={{
                                            flex: "0 0 7%",
                                            height: "17px",
                                            width: "17px",
                                            background: selected,
                                            borderRadius: "4px",
                                            marginRight: "10px",
                                          }}
                                        ></span>
                                      );
                                    }}
                                  >
                                    {colorSet.map((color) => (
                                      <MenuItem
                                        key={color.value}
                                        value={color.value}
                                      >
                                        <span
                                          style={{
                                            flex: "0 0 7%",
                                            height: "17px",
                                            width: "17px",
                                            background: color.value,
                                            borderRadius: "4px",
                                            marginRight: "10px",
                                          }}
                                        ></span>
                                        {color.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </span>
                                <input
                                  type="text"
                                  placeholder="Label Name"
                                  onKeyDown={(event) => event.stopPropagation()}
                                  value={labelName}
                                  onChange={handleLabelNameChange}
                                  maxLength={50}
                                  style={{
                                    padding: "8px 9px 9px 15px",
                                    fontSize: "16px",
                                    outline: "none",
                                    margin: "5px 0",
                                    width: "85%",
                                    flex: "0 0 75%",
                                    backgroundColor: "#F5F5F5",
                                    border: "1px solid grey",
                                    borderRadius: "4px",
                                  }}
                                ></input>
                              </div>

                              <Box sx={{ padding: "2px" }}>
                                <Button type="submit">Save</Button>
                                <Button onClick={cancelLabelCreation}>
                                  Cancel
                                </Button>
                              </Box>
                            </form>
                          </div>
                        )}
                        {labels.map((label) => (
                          <MenuItem
                            value={label.id}
                            id={label.id}
                            key={label.id}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                width: "100%",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <span
                                  style={{
                                    height: "17px",
                                    width: "17px",
                                    minWidth: "17px",
                                    background: label.labelColor,
                                    borderRadius: "4px",
                                    marginRight: "10px",
                                    display: "inline-block",
                                  }}
                                ></span>
                                <abbr
                                  title={label.name}
                                  style={{
                                    textOverflow: "ellipsis",
                                    textDecoration: "none",
                                    width: "178px",
                                    overflow: "hidden",
                                  }}
                                >
                                  {label.name}
                                </abbr>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, label.id)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <MenuItem onClick={handleEditProjectLabel}>
                          Edit
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setDeleteProjectLabelModal(true);
                            localStorage.setItem("popupOpen", true.toString());
                          }}
                        >
                          Delete
                        </MenuItem>
                      </Menu>
                    </div>
                    <Box
                      onDragOver={(e: any) => {
                        if (!selectedLabels[idx]) {
                          // Don't show error on dragOver to prevent duplicate messages
                          return;
                        }
                        e.preventDefault(); // Prevent default to allow drop
                        e.stopPropagation(); // Stop bubbling
                      }}
                      key={id}
                      onDrop={(e: any) => {
                        if (!selectedLabels[idx]) {
                          toast.error(
                            "Please select a label before browsing files"
                          );
                          return;
                        }
                        e.preventDefault();
                        e.stopPropagation(); // Stop bubbling
                        if (e.dataTransfer?.files?.length) {
                          handleFileChange(
                            idx,
                            e as unknown as ChangeEvent<HTMLInputElement>
                          );
                        }
                      }}
                      onClick={(e) => {
                        // Don't show error here to prevent duplicate messages with Browse button
                        if (!selectedLabels[idx]) {
                          return;
                        }
                        e.stopPropagation(); // Stop bubbling
                        fileInputRefs.current[idx]?.click();
                      }}
                    >
                      <Box display="flex" justifyContent="center">
                        <IconButton>
                          <VisionStudioSvg icon={fileUploadIcon} />
                          <input
                            hidden
                            accept="image/*"
                            ref={(el) => (fileInputRefs.current[idx] = el)}
                            type="file"
                            onChange={(e) => {
                              if (!selectedLabels[idx]) {
                                toast.error(
                                  "Please select a label before browsing files"
                                );
                                return;
                              }
                              handleFileChange(
                                idx,
                                e as ChangeEvent<HTMLInputElement>
                              );
                            }}
                          />
                        </IconButton>
                      </Box>
                      <Box display="flex" width="100%" justifyContent="center">
                        {uploadLength[selectedLabels[idx]] > 0 && (
                          <Typography
                            variant="h6"
                            marginTop="0px"
                            sx={{
                              fontSize: "12px",
                              color: "#3e64ad",
                            }}
                          >
                            {uploadLength[selectedLabels[idx]]} Images Uploaded
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" width="100%" justifyContent="center">
                        <Typography
                          variant="h6"
                          marginTop="0px"
                          sx={{
                            fontSize: "12px",
                            color: "#132A00",
                          }}
                        >
                          Drop your image here, or
                          <Button
                            sx={{
                              fontSize: "12px",
                              textTransform: "initial",
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // Stop event bubbling
                              if (!selectedLabels[idx]) {
                                toast.error(
                                  "Please select a label before browsing files"
                                );
                                return;
                              }

                              // trigger file input only if validation passes
                              fileInputRefs.current[idx]?.click();
                            }}
                          >
                            Browse
                          </Button>
                          <input
                            hidden
                            accept="image/*"
                            ref={(el) => (fileInputRefs.current[idx] = el)}
                            type="file"
                            multiple
                            onChange={(e) => {
                              handleFileChange(
                                idx,
                                e as ChangeEvent<HTMLInputElement>
                              );
                            }}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              ))}
            </Box>
          </StyledBox>
        </Box>
      )}
      <Dialog
        open={deleteProjectLabelModal}
        onClose={() => {
          setDeleteProjectLabelModal(false);
          localStorage.setItem("popupOpen", false.toString());
        }}
        PaperProps={{
          style: {
            boxShadow: "none",
            width: "50%",
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "#2121210f",
            },
          },
        }}
      >
        <DialogTitle>Delete Project Label</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to delete this Label?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="calcelDeleteLabel" onClick={handleDeleteLabelClose}>
            Cancel
          </Button>
          <Button id="confirmDeleteLabel" onClick={handleProjectLabelDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {imageSrcs.length > 0 && (
        <Box
          sx={{
            border: "1px solid #E4E9F0",
            padding: "12px",
            borderRadius: "12px",
            width: "96%",
          }}
        >
          <UploadedImage
            imageUrl={imageSrcs}
            files={files}
            progress={progress}
          />
        </Box>
      )}
    </>
  );
};

export default PreDataSetImageUpload;
